var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
import { cloneDeep, defaults } from "lodash-es";
import { format } from "date-fns";
const state = {
  reloading: false,
  loaded: false,
  errored: false,
  options: {},
  preselectedFilters: [],
  search: {
    query: "",
    filters: [],
    page: 0,
    sortOption: null,
    extended: {}
  },
  results: {},
  previousItemResults: []
};
const getters = {
  loaded: ({ loaded }) => loaded,
  reloading: ({ reloading }) => reloading,
  results: ({ results }) => results,
  query: ({ search }) => search.query,
  sort: ({ search }) => search.sortOption,
  filters: ({ search }) => search.filters,
  currentPage: ({ search }) => search.page,
  extended: ({ search }) => search.extended
};
const mutations = {
  setLoaded(state2) {
    state2.loaded = true;
    state2.reloading = false;
  },
  setReloading(state2) {
    state2.reloading = true;
  },
  setErrored(state2) {
    state2.errored = true;
  },
  updateQueryParams(state2) {
    const url = new URL(window.location.href);
    const search_params = new URLSearchParams(url.search);
    Object.entries(state2.search).forEach(([key, value]) => {
      if (key === "extended")
        return;
      if (Array.isArray(value))
        value = createFilterString(value);
      else if (typeof value === "object")
        value = false;
      else if (typeof value === "string")
        value = value.trim();
      if (state2.options.preserveResults && key === "page")
        search_params.delete(key);
      else if (value)
        search_params.set(key, value);
      else
        search_params.delete(key);
    });
    const paramsExist = !search_params.keys().next().done;
    const newURL = `${url.pathname}${paramsExist ? `?${search_params.toString()}` : ""}`;
    history.replaceState({}, "", decodeURIComponent(newURL));
  },
  applyOptions(state2, payload) {
    state2.options = payload;
  },
  parseInitialQueryParams(state2) {
    const url = new URL(window.location.href);
    const search_params = new URLSearchParams(url.search);
    search_params.forEach((value, key) => {
      if (!value)
        return;
      if (!state2.search.hasOwnProperty(key))
        return;
      if (key === "filters") {
        state2.preselectedFilters = value.split(",");
      } else if (key === "page") {
        if (state2.options.preserveResults)
          return;
      } else {
        if (isNaN(value))
          state2.search[key] = value;
        else
          state2.search[key] = Number(value);
      }
    });
  },
  setResults(state2, payload) {
    if (state2.options.preserveResults) {
      state2.previousItemResults.push(...payload.itemResults);
      payload.itemResults = state2.previousItemResults;
    }
    state2.results = payload;
    state2.search.filters = cloneDeep(payload.filters);
    state2.search.query = payload.queryUsed.searchTerm;
    state2.search.sortOption = payload.queryUsed.sortOption;
    state2.search.page = payload.queryUsed.currentPage;
    state2.search.extended = payload.extended ? cloneDeep(payload.extended) : payload.queryUsed.extended;
    state2.preselectedFilters = [];
  },
  setQuery(state2, payload) {
    state2.search.query = payload;
    state2.search.page = 0;
  },
  resetAllFilters(state2) {
    const groups = state2.search.filters;
    state2.search.page = 0;
    const recurse = (filters) => {
      return filters.forEach((f) => {
        f.selected = false;
        if (f.filters && f.filters.length)
          recurse(f.filters);
      });
    };
    groups.forEach((g) => recurse(g.filters));
  },
  setFilterValue(state2, { filter, value }) {
    const groups = state2.search.filters;
    state2.search.page = 0;
    const recurse = (filters) => {
      return filters.some((f) => {
        if (f.id === filter.id)
          return f.selected = value, true;
        if (f.filters && f.filters.length)
          return recurse(f.filters);
        return false;
      });
    };
    groups.some((g) => {
      return recurse(g.filters);
    });
  },
  setPageIndex(state2, payload) {
    state2.search.page = payload;
  },
  setSortOrder(state2, payload) {
    state2.search.sortOption = payload;
    state2.search.page = 0;
  },
  clearPreviousItemResults(state2) {
    state2.previousItemResults = [];
  },
  extend(state2, callback) {
    state2.search.extended = cloneDeep(callback(state2.search.extended));
  },
  clearSearchParameters(state2) {
    state2.loaded = false;
    state2.preselectedFilters = [];
    state2.search.query = "";
    state2.search.filters = [];
    state2.search.page = 0;
    state2.search.sortOption = null;
    state2.search.extended = {};
    state2.results = {};
    state2.previousItemResults = [];
  }
};
const actions = {
  changeSort({ commit, dispatch }, sort) {
    commit("setSortOrder", sort);
    commit("clearPreviousItemResults");
    return dispatch("executeSearch");
  },
  goToPage({ commit, dispatch }, index) {
    commit("setPageIndex", index);
    return dispatch("executeSearch");
  },
  removeAllTags({ commit, dispatch }) {
    commit("resetAllFilters");
    commit("clearPreviousItemResults");
    return dispatch("executeSearch");
  },
  removeTagItem({ commit, dispatch }, tag) {
    commit("setFilterValue", { filter: tag, value: false });
    commit("clearPreviousItemResults");
    return dispatch("executeSearch");
  },
  resetSearch({ commit, dispatch }, callback = (q) => q) {
    commit("clearSearchParameters");
    dispatch("executeSearch", callback);
  },
  executeSearch({ commit, state: state2 }, callback = (q) => q) {
    return new Promise(async (resolve) => {
      const start = new Date().getTime();
      commit("setReloading");
      let transformed;
      let data = __spreadValues({
        searchIndex: state2.options.searchIndex,
        searchTerm: state2.search.query,
        filters: state2.search.filters,
        currentPage: state2.search.page,
        pageSize: state2.options.pageLength,
        preselectedFilters: state2.preselectedFilters,
        extended: state2.search.extended
      }, state2.search.sortOption && {
        sortOption: state2.search.sortOption
      });
      data.searchTerm = String(data.searchTerm);
      if (callback && typeof callback === "function")
        transformed = callback(data);
      if (transformed)
        data = cloneDeep(transformed);
      data = JSON.stringify(data);
      const response = await fetch(state2.options.endpoint, {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json"
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: data
      });
      if (response.ok) {
        const results = await response.json();
        commit("setResults", results);
        commit("updateQueryParams");
        const elapsed = new Date().getTime() - start;
        const timeout = elapsed < 750 ? 750 - elapsed : 0;
        setTimeout(() => {
          commit("setLoaded");
          resolve();
        }, timeout);
      } else {
        commit("setErrored");
        resolve();
      }
    });
  }
};
const store = {
  state,
  getters,
  mutations,
  actions
};
function createFilterString(filters) {
  const ids = [];
  const recurse = (filters2) => {
    return filters2.reduce((_selections, f) => {
      if (f.selected == true)
        _selections.push(f.id);
      if (f.filters && f.filters.length) {
        let childSelections = recurse(f.filters);
        if (childSelections.length)
          _selections.push(...childSelections);
      }
      return _selections;
    }, []);
  };
  filters.forEach((g) => {
    let selections = recurse(g.filters);
    if (selections.length) {
      ids.push(...selections);
    }
  });
  return ids.join(",");
}
var render$d = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("transition", { attrs: { "name": "navigate", "appear": "", "mode": "out-in" } }, [_vm._t("default")], 2);
};
var staticRenderFns$d = [];
var Navigate_vue_vue_type_style_index_0_lang = "";
function normalizeComponent(scriptExports, render2, staticRenderFns2, functionalTemplate, injectStyles, scopeId, moduleIdentifier, shadowMode) {
  var options = typeof scriptExports === "function" ? scriptExports.options : scriptExports;
  if (render2) {
    options.render = render2;
    options.staticRenderFns = staticRenderFns2;
    options._compiled = true;
  }
  if (functionalTemplate) {
    options.functional = true;
  }
  if (scopeId) {
    options._scopeId = "data-v-" + scopeId;
  }
  var hook;
  if (moduleIdentifier) {
    hook = function(context) {
      context = context || this.$vnode && this.$vnode.ssrContext || this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext;
      if (!context && typeof __VUE_SSR_CONTEXT__ !== "undefined") {
        context = __VUE_SSR_CONTEXT__;
      }
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode ? function() {
      injectStyles.call(this, (options.functional ? this.parent : this).$root.$options.shadowRoot);
    } : injectStyles;
  }
  if (hook) {
    if (options.functional) {
      options._injectStyles = hook;
      var originalRender = options.render;
      options.render = function renderWithStyleInjection(h, context) {
        hook.call(context);
        return originalRender(h, context);
      };
    } else {
      var existing = options.beforeCreate;
      options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
    }
  }
  return {
    exports: scriptExports,
    options
  };
}
const __vue2_script$d = {
  name: "transition-navigate"
};
const __cssModules$d = {};
var __component__$d = /* @__PURE__ */ normalizeComponent(__vue2_script$d, render$d, staticRenderFns$d, false, __vue2_injectStyles$d, null, null, null);
function __vue2_injectStyles$d(context) {
  for (let o in __cssModules$d) {
    this[o] = __cssModules$d[o];
  }
}
var TransitionNavigate = /* @__PURE__ */ function() {
  return __component__$d.exports;
}();
var render$c = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  _vm._self._c || _h;
  return _vm._m(0);
};
var staticRenderFns$c = [function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "loader" }, [_c("div"), _c("div"), _c("div"), _c("div"), _c("div"), _c("div"), _c("div"), _c("div")]);
}];
var Loader_vue_vue_type_style_index_0_scoped_true_lang = "";
const __vue2_script$c = {
  name: "loader"
};
const __cssModules$c = {};
var __component__$c = /* @__PURE__ */ normalizeComponent(__vue2_script$c, render$c, staticRenderFns$c, false, __vue2_injectStyles$c, "0fb452d6", null, null);
function __vue2_injectStyles$c(context) {
  for (let o in __cssModules$c) {
    this[o] = __cssModules$c[o];
  }
}
var SearchLoader = /* @__PURE__ */ function() {
  return __component__$c.exports;
}();
var render$b = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "search", attrs: { "id": "bluesearch" } }, [_c("TransitionNavigate", [_vm.loaded ? [_vm._t("default", function() {
    return [_c("div", { staticClass: "search__layout", attrs: { "data-hide-rail": _vm.options.hideRail } }, [_vm._t("header", function() {
      return [_vm.options.showQuery ? _c("header", { staticClass: "search__header" }, [_c("SearchQuery")], 1) : _vm._e()];
    }), !_vm.options.hideRail ? _vm._t("rail", function() {
      return [_vm.options.showFilters ? _c("aside", { staticClass: "search__rail" }, [_c("SearchFilters")], 1) : _vm._e()];
    }) : _vm._e(), _vm._t("main", function() {
      return [_c("section", { staticClass: "search__main" }, [_vm._t("tags", function() {
        return [_c("SearchTags")];
      }), _vm._t("results", function() {
        return [_c("SearchResults")];
      })], 2)];
    }), _vm._t("footer", function() {
      return [_c("footer", { staticClass: "search__footer" }, [_c("SearchPagination")], 1)];
    })], 2)];
  }, { "loaded": _vm.loaded, "reloading": _vm.reloading, "executeSearch": _vm.executeSearch, "results": _vm.results, "extend": _vm.extend, "extended": _vm.extended })] : [_vm._t("loader", function() {
    return [_c("SearchLoader")];
  }, { "loaded": _vm.loaded })]], 2)], 1);
};
var staticRenderFns$b = [];
var BlueSearch_vue_vue_type_style_index_0_lang = "";
var BlueSearch_vue_vue_type_style_index_1_scoped_true_lang = "";
const __vue2_script$b = {
  name: "BlueSearch",
  components: { TransitionNavigate, SearchLoader },
  data() {
    return {
      preSearch: null
    };
  },
  computed: {
    reloading() {
      return this.$store.getters.reloading;
    },
    loaded() {
      return this.$store.getters.loaded && !this.$store.getters.errored;
    },
    results() {
      return this.$store.getters.results;
    },
    extended() {
      return this.$store.getters.extended;
    }
  },
  methods: {
    executeSearch(callback) {
      return this.$store.dispatch("executeSearch", callback);
    },
    extend(callback) {
      this.$store.commit("extend", callback);
    }
  },
  created() {
    this.$store.commit("applyOptions", this.options);
    this.$store.commit("parseInitialQueryParams");
    if (!this.options.allowPreSearch)
      return;
    this.preSearch = new Promise((resolve) => {
      let isResolved = false;
      const done = () => {
        isResolved = true;
        resolve();
      };
      this.$emit("presearch", {
        done,
        extend: this.extend,
        extended: this.extended
      });
      setTimeout(() => {
        if (!isResolved) {
          done();
        }
      }, 1500);
    });
  },
  async mounted() {
    if (this.options.allowPreSearch) {
      await this.preSearch;
    }
    this.$store.dispatch("executeSearch");
  }
};
const __cssModules$b = {};
var __component__$b = /* @__PURE__ */ normalizeComponent(__vue2_script$b, render$b, staticRenderFns$b, false, __vue2_injectStyles$b, "97028a78", null, null);
function __vue2_injectStyles$b(context) {
  for (let o in __cssModules$b) {
    this[o] = __cssModules$b[o];
  }
}
var BlueSearchApp = /* @__PURE__ */ function() {
  return __component__$b.exports;
}();
var render$a = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("span", { staticClass: "root" }, [_vm._t("default", function() {
    return [_c("div", { staticClass: "search-query" }, [_c("div", { staticClass: "search-query__form" }, [_c("div", { staticClass: "search-query__wrapper" }, [_c("input", { directives: [{ name: "model", rawName: "v-model", value: _vm.queryModel, expression: "queryModel" }], staticClass: "search-query__input", attrs: { "placeholder": _vm.options.queryPlaceholderText, "type": "text" }, domProps: { "value": _vm.queryModel }, on: { "keyup": function($event) {
      if (!$event.type.indexOf("key") && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
        return null;
      }
      return _vm.executeSearch();
    }, "input": function($event) {
      if ($event.target.composing) {
        return;
      }
      _vm.queryModel = $event.target.value;
    } } }), _vm.options.showReset ? _c("button", { staticClass: "search-query__reset", attrs: { "type": "button" }, on: { "click": function($event) {
      return _vm.resetSearch();
    } } }, [_c("span", [_vm._v(_vm._s(_vm.options.resetText))])]) : _vm._e()]), _c("button", { staticClass: "search-query__submit", attrs: { "type": "submit" }, on: { "click": function($event) {
      return _vm.executeSearch();
    } } }, [_c("span", [_vm._v(_vm._s(_vm.options.querySubmitText))])]), _vm.options.showSort ? _c("div", { staticClass: "search-query__sort" }, [_c("select", { directives: [{ name: "model", rawName: "v-model", value: _vm.sortModel, expression: "sortModel" }], on: { "change": function($event) {
      var $$selectedVal = Array.prototype.filter.call($event.target.options, function(o) {
        return o.selected;
      }).map(function(o) {
        var val = "_value" in o ? o._value : o.value;
        return val;
      });
      _vm.sortModel = $event.target.multiple ? $$selectedVal : $$selectedVal[0];
    } } }, _vm._l(_vm.results.sortOptions, function(option) {
      return _c("option", { domProps: { "value": option } }, [_vm._v("Sort by " + _vm._s(option.displayName))]);
    }), 0)]) : _vm._e()]), _c("small", { staticClass: "search-query__results" }, [_vm._v(_vm._s(_vm.label))])])];
  }, { "results": _vm.results, "query": _vm.query, "setQuery": _vm.setQuery, "sort": _vm.sort, "setSort": _vm.setSort, "resetSearch": _vm.resetSearch, "currentResultsStart": _vm.currentResultsStart, "currentResultsEnd": _vm.currentResultsEnd, "clearPreviousItemResults": _vm.clearPreviousItemResults, "executeSearch": _vm.executeSearch, "extend": _vm.extend, "extended": _vm.extended })], 2);
};
var staticRenderFns$a = [];
var Query_vue_vue_type_style_index_0_scoped_true_lang = "";
const __vue2_script$a = {
  name: "BlueQuery",
  components: {},
  data() {
    return {};
  },
  computed: {
    currentResultsStart() {
      let totalResults = this.results.totalResultCount;
      if (this.results.currentPage == 0 || this.options.preserveResults) {
        if (totalResults)
          return 1;
        return 0;
      }
      return this.results.currentPage * this.results.pageSize + 1;
    },
    currentResultsEnd() {
      let totalResults = this.results.totalResultCount;
      let currentPage = this.results.currentPage;
      let pageSize = this.results.pageSize;
      let max = (currentPage + 1) * pageSize;
      if (currentPage == 0) {
        if (totalResults <= pageSize) {
          return totalResults;
        }
        return pageSize;
      } else {
        if (max > totalResults)
          return totalResults;
        return max;
      }
    },
    label() {
      return `Results ${this.currentResultsStart} - ${this.currentResultsEnd} of ${this.results.totalResultCount}`;
    },
    results() {
      return this.$store.getters.results;
    },
    sort() {
      return this.$store.getters.sort;
    },
    sortModel: {
      get() {
        return this.sort;
      },
      set(val) {
        this.setSort(val);
      }
    },
    query() {
      return this.$store.getters.query;
    },
    queryModel: {
      get() {
        return this.query;
      },
      set(val) {
        this.setQuery(val);
      }
    },
    extended() {
      return this.$store.getters.extended;
    }
  },
  methods: {
    clearPreviousItemResults() {
      this.$store.commit("clearPreviousItemResults");
      this.$store.commit("setPageIndex", 0);
    },
    setSort(val) {
      let value;
      if (typeof val === "object" && val.hasOwnProperty("target"))
        value = val.target.value;
      else
        value = val;
      this.$store.dispatch("changeSort", value);
    },
    setQuery(val) {
      let value;
      if (typeof val === "object")
        value = val.target.value;
      else
        value = val;
      this.$store.commit("setQuery", value);
    },
    resetSearch(callback) {
      this.$store.dispatch("resetSearch", callback);
    },
    extend(callback) {
      this.$store.commit("extend", callback);
    },
    executeSearch(callback) {
      if (this.options.preserveResults)
        this.$store.commit("clearPreviousItemResults");
      return this.$store.dispatch("executeSearch", callback);
    }
  },
  created() {
  },
  mounted() {
  }
};
const __cssModules$a = {};
var __component__$a = /* @__PURE__ */ normalizeComponent(__vue2_script$a, render$a, staticRenderFns$a, false, __vue2_injectStyles$a, "4b5fb752", null, null);
function __vue2_injectStyles$a(context) {
  for (let o in __cssModules$a) {
    this[o] = __cssModules$a[o];
  }
}
var SearchQuery = /* @__PURE__ */ function() {
  return __component__$a.exports;
}();
var render$9 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("transition-group", { attrs: { "name": "expand" }, on: { "enter": _vm.enter, "after-enter": _vm.afterEnter, "leave": _vm.leave } }, [_vm._t("default")], 2);
};
var staticRenderFns$9 = [];
var Expand_vue_vue_type_style_index_0_scoped_true_lang = "";
var Expand_vue_vue_type_style_index_1_lang = "";
const __vue2_script$9 = {
  name: "transition-expand",
  methods: {
    enter(element) {
      const width = getComputedStyle(element).width;
      element.style.width = width;
      element.style.position = "absolute";
      element.style.visibility = "hidden";
      element.style.height = "auto";
      const height = getComputedStyle(element).height;
      element.style.width = null;
      element.style.position = null;
      element.style.visibility = null;
      element.style.height = 0;
      getComputedStyle(element).height;
      requestAnimationFrame(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = "auto";
    },
    leave(element) {
      const height = getComputedStyle(element).height;
      element.style.height = height;
      getComputedStyle(element).height;
      requestAnimationFrame(() => {
        element.style.height = 0;
      });
    }
  }
};
const __cssModules$9 = {};
var __component__$9 = /* @__PURE__ */ normalizeComponent(__vue2_script$9, render$9, staticRenderFns$9, false, __vue2_injectStyles$9, "ee6a5b2c", null, null);
function __vue2_injectStyles$9(context) {
  for (let o in __cssModules$9) {
    this[o] = __cssModules$9[o];
  }
}
var TransitionExpand = /* @__PURE__ */ function() {
  return __component__$9.exports;
}();
var render$8 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "search-filters-group" }, [_c("div", { staticClass: "search-filters-group__title", class: { active: _vm.active }, attrs: { "role": "button", "tabindex": "0" }, on: { "click": function($event) {
    $event.stopPropagation();
    $event.preventDefault();
    _vm.active = !_vm.active;
  }, "keyup": function($event) {
    if (!$event.type.indexOf("key") && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
      return null;
    }
    $event.stopPropagation();
    $event.preventDefault();
    _vm.active = !_vm.active;
  } } }, [_vm._v(_vm._s(_vm.group.name))]), _c("TransitionExpand", [_vm.active ? _c("div", { key: "filterGroup", staticClass: "search-filters-group__options" }, [_c("div", { staticClass: "search-filters-group__options-inner" }, [_vm._t("default")], 2)]) : _vm._e()])], 1);
};
var staticRenderFns$8 = [];
var Group_vue_vue_type_style_index_0_scoped_true_lang = "";
const __vue2_script$8 = {
  name: "BlueFilterGroup",
  components: { TransitionExpand },
  props: ["group", "initiallyExpanded"],
  data() {
    return {
      active: this.initiallyExpanded
    };
  },
  created() {
    const recurse = (filters) => {
      let hasSelections = false;
      filters.forEach((filter) => {
        if (filter.selected) {
          hasSelections = true;
        } else if (filter.filters && filter.filters.length) {
          if (recurse(filter.filters))
            hasSelections = true;
        }
      });
      return hasSelections;
    };
    if (recurse(this.group.filters))
      this.active = true;
  }
};
const __cssModules$8 = {};
var __component__$8 = /* @__PURE__ */ normalizeComponent(__vue2_script$8, render$8, staticRenderFns$8, false, __vue2_injectStyles$8, "58b7279e", null, null);
function __vue2_injectStyles$8(context) {
  for (let o in __cssModules$8) {
    this[o] = __cssModules$8[o];
  }
}
var Group = /* @__PURE__ */ function() {
  return __component__$8.exports;
}();
var render$7 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.filter.count > 0 || _vm.model ? _c("div", { staticClass: "search-filters-item" }, [_c("input", { directives: [{ name: "model", rawName: "v-model", value: _vm.model, expression: "model" }], staticClass: "search-filters-item__input", attrs: { "type": "checkbox", "id": _vm.filter.id }, domProps: { "checked": Array.isArray(_vm.model) ? _vm._i(_vm.model, null) > -1 : _vm.model }, on: { "change": function($event) {
    var $$a = _vm.model, $$el = $event.target, $$c = $$el.checked ? true : false;
    if (Array.isArray($$a)) {
      var $$v = null, $$i = _vm._i($$a, $$v);
      if ($$el.checked) {
        $$i < 0 && (_vm.model = $$a.concat([$$v]));
      } else {
        $$i > -1 && (_vm.model = $$a.slice(0, $$i).concat($$a.slice($$i + 1)));
      }
    } else {
      _vm.model = $$c;
    }
  } } }), _c("label", { staticClass: "search-filters-item__label", attrs: { "for": _vm.filter.id } }, [_c("span", [_vm._v(_vm._s(_vm.label))])]), _vm.hasChildren ? _c("button", { staticClass: "search-filters-item__expand", class: { active: _vm.active }, attrs: { "type": "button" }, on: { "click": function($event) {
    $event.stopPropagation();
    $event.preventDefault();
    _vm.active = !_vm.active;
  }, "keydown": function($event) {
    if (!$event.type.indexOf("key") && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
      return null;
    }
    $event.stopPropagation();
    $event.preventDefault();
    _vm.active = !_vm.active;
  } } }) : _vm._e(), _vm.hasChildren ? _c("TransitionExpand", [_vm.active ? _c("div", { key: _vm.label + "-subFilters", staticClass: "search-filters-item__filters" }, [_c("div", { staticClass: "search-filters-item__filters-inner" }, _vm._l(_vm.filter.filters, function(item) {
    return _c("BlueFilterItem", { key: item.id, attrs: { "filter": item, "setFilterValue": _vm.setFilterValue } });
  }), 1)]) : _vm._e()]) : _vm._e()], 1) : _vm._e();
};
var staticRenderFns$7 = [];
var Item_vue_vue_type_style_index_0_scoped_true_lang$3 = "";
const __vue2_script$7 = {
  name: "BlueFilterItem",
  components: { TransitionExpand },
  props: ["filter", "setFilterValue"],
  data() {
    return {
      active: false
    };
  },
  computed: {
    hasChildren() {
      return this.filter.filters && this.filter.filters.length;
    },
    label() {
      if (this.options.showFiltersCount) {
        return `${this.filter.value} (${this.filter.count})`;
      }
      return this.filter.value;
    },
    reloading() {
      return this.$store.getters.reloading;
    },
    model: {
      get() {
        return this.filter.selected;
      },
      set(value) {
        this.setFilterValue(this.filter, value);
      }
    }
  },
  methods: {
    parseTreeActiveState(filters) {
      let hasSelections = false;
      filters.forEach((filter) => {
        if (filter.selected)
          hasSelections = true;
        else if (filter.filters && filter.filters.length) {
          if (this.parseTreeActiveState(filter.filters))
            hasSelections = true;
        }
      });
      return hasSelections;
    }
  },
  watch: {
    reloading() {
      if (this.hasChildren) {
        this.active = this.parseTreeActiveState(this.filter.filters);
      }
    }
  },
  created() {
    if (this.hasChildren) {
      if (this.parseTreeActiveState(this.filter.filters)) {
        this.active = true;
      }
    }
  }
};
const __cssModules$7 = {};
var __component__$7 = /* @__PURE__ */ normalizeComponent(__vue2_script$7, render$7, staticRenderFns$7, false, __vue2_injectStyles$7, "07d7c509", null, null);
function __vue2_injectStyles$7(context) {
  for (let o in __cssModules$7) {
    this[o] = __cssModules$7[o];
  }
}
var Item$3 = /* @__PURE__ */ function() {
  return __component__$7.exports;
}();
var render$6 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("span", { staticClass: "root" }, [_vm._t("default", function() {
    return [_c("div", { staticClass: "search-filters" }, [_vm._t("mobileDrawerToggle", function() {
      return [_c("button", { staticClass: "search-filters__toggle", attrs: { "disabled": _vm.filters.length ? false : "disabled", "tabindex": _vm.filters.length ? false : "-1" }, on: { "click": _vm.toggleMobileDrawer } }, [_vm._v(_vm._s(_vm.options.filterDrawerToggleText))])];
    }, { "toggleMobileDrawer": _vm.toggleMobileDrawer, "mobileDrawerOpen": _vm.mobileDrawerOpen }), _c("div", { staticClass: "search-filters__inner", class: { active: _vm.mobileDrawerOpen }, on: { "click": function($event) {
      if ($event.target !== $event.currentTarget) {
        return null;
      }
      return _vm.toggleMobileDrawer(false);
    } } }, [_vm._t("mobileDrawerClose", function() {
      return [_c("button", { staticClass: "search-filters__close", on: { "click": function($event) {
        return _vm.toggleMobileDrawer(false);
      } } }, [_c("span", [_vm._v(_vm._s(_vm.options.filterDrawerCloseText))])])];
    }, { "toggleMobileDrawer": _vm.toggleMobileDrawer, "mobileDrawerOpen": _vm.mobileDrawerOpen }), _c("div", { staticClass: "search-filters__content" }, [_c("div", { staticClass: "search-filters__form" }, [_vm._l(_vm.filters, function(group) {
      return [_vm._t("group", null, { "group": group, "initiallyExpanded": _vm.options.filterInitiallyExpanded, "setFilterValue": _vm.setFilterValue, "extend": _vm.extend, "extended": _vm.extended }), _c("Group", { attrs: { "group": group, "initiallyExpanded": _vm.options.filterInitiallyExpanded, "setFilterValue": _vm.setFilterValue } }, [_vm._l(group.filters, function(filter) {
        return [_vm._t("item", function() {
          return [_c("Item", { attrs: { "filter": filter, "setFilterValue": _vm.setFilterValue } })];
        }, { "filter": filter, "setFilterValue": _vm.setFilterValue, "extend": _vm.extend, "extended": _vm.extended })];
      })], 2)];
    }), !_vm.options.filterAutoSubmit ? _vm._t("submit", function() {
      return [_c("button", { staticClass: "search-filters__btn", attrs: { "disabled": _vm.filters.length ? false : "disabled", "tabindex": _vm.filters.length ? false : "-1" }, on: { "click": function($event) {
        _vm.executeSearch();
        _vm.toggleMobileDrawer(false);
      } } }, [_vm._v(_vm._s(_vm.options.filterSubmitText))])];
    }, { "toggleMobileDrawer": _vm.toggleMobileDrawer, "executeSearch": _vm.executeSearch, "filters": _vm.filters, "filterSubmitText": _vm.options.filterSubmitText }) : _vm._e()], 2)])], 2)], 2)];
  }, { "executeSearch": _vm.executeSearch, "filters": _vm.filters, "setFilterValue": _vm.setFilterValue, "clearAllFilters": _vm.clearAllFilters, "extend": _vm.extend, "extended": _vm.extended })], 2);
};
var staticRenderFns$6 = [];
var Filters_vue_vue_type_style_index_0_scoped_true_lang = "";
const __vue2_script$6 = {
  name: "BlueFilters",
  components: { Group, Item: Item$3 },
  data() {
    return {
      mobileDrawerOpen: false
    };
  },
  computed: {
    filters() {
      return this.$store.getters.filters.filter((g) => g.visible ? true : g.filters.some((f) => f.selected));
    },
    extended() {
      return this.$store.getters.extended;
    }
  },
  methods: {
    clearAllFilters() {
      this.$store.dispatch("removeAllTags");
    },
    toggleMobileDrawer(toggle = !this.mobileDrawerOpen) {
      this.mobileDrawerOpen = toggle;
    },
    setFilterValue(filter, val, callback) {
      let value;
      if (typeof val === "object")
        value = val.target.value;
      else
        value = val;
      this.$store.commit("setFilterValue", { filter, value });
      if (this.options.filterAutoSubmit)
        return this.executeSearch(callback);
    },
    extend(callback) {
      this.$store.commit("extend", callback);
    },
    executeSearch(callback) {
      if (this.options.preserveResults)
        this.$store.commit("clearPreviousItemResults");
      return this.$store.dispatch("executeSearch", callback);
    }
  }
};
const __cssModules$6 = {};
var __component__$6 = /* @__PURE__ */ normalizeComponent(__vue2_script$6, render$6, staticRenderFns$6, false, __vue2_injectStyles$6, "64761274", null, null);
function __vue2_injectStyles$6(context) {
  for (let o in __cssModules$6) {
    this[o] = __cssModules$6[o];
  }
}
var SearchFilters = /* @__PURE__ */ function() {
  return __component__$6.exports;
}();
var render$5 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("button", { staticClass: "search__tag", on: { "click": function($event) {
    return _vm.removeTag(_vm.tag);
  } } }, [_vm._v(_vm._s(_vm.tag.value))]);
};
var staticRenderFns$5 = [];
var Item_vue_vue_type_style_index_0_scoped_true_lang$2 = "";
const __vue2_script$5 = {
  name: "BlueTagItem",
  props: ["tag", "removeTag"]
};
const __cssModules$5 = {};
var __component__$5 = /* @__PURE__ */ normalizeComponent(__vue2_script$5, render$5, staticRenderFns$5, false, __vue2_injectStyles$5, "7d6af8ba", null, null);
function __vue2_injectStyles$5(context) {
  for (let o in __cssModules$5) {
    this[o] = __cssModules$5[o];
  }
}
var Item$2 = /* @__PURE__ */ function() {
  return __component__$5.exports;
}();
var render$4 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("span", { staticClass: "root" }, [_vm._t("default", function() {
    return [_vm.tags.length ? _c("div", { staticClass: "search__tags" }, [_vm._l(_vm.tags, function(tag) {
      return [_vm._t("item", function() {
        return [_c("Item", { attrs: { "tag": tag, "removeTag": _vm.removeTag } })];
      }, { "tag": tag, "removeTag": _vm.removeTag })];
    })], 2) : _vm._e()];
  }, { "tags": _vm.tags, "removeTag": _vm.removeTag, "clearAllTags": _vm.clearAllTags })], 2);
};
var staticRenderFns$4 = [];
var Tags_vue_vue_type_style_index_0_scoped_true_lang = "";
const __vue2_script$4 = {
  name: "BlueTags",
  components: { Item: Item$2 },
  computed: {
    tags() {
      const recurse = (filters) => {
        const tags = [];
        filters.forEach((f) => {
          if (f.selected) {
            tags.push(f);
          }
          if (f.filters) {
            let _tags = recurse(f.filters);
            if (_tags.length) {
              tags.push(..._tags);
            }
          }
        });
        return tags;
      };
      return this.$store.getters.results.filters.reduce((_tags, g) => {
        const tags = recurse(g.filters);
        _tags.push(...tags);
        return _tags;
      }, []);
    }
  },
  methods: {
    clearAllTags() {
      this.$store.dispatch("removeAllTags");
    },
    removeTag(tag) {
      this.$store.dispatch("removeTagItem", tag);
    }
  }
};
const __cssModules$4 = {};
var __component__$4 = /* @__PURE__ */ normalizeComponent(__vue2_script$4, render$4, staticRenderFns$4, false, __vue2_injectStyles$4, "59e80f22", null, null);
function __vue2_injectStyles$4(context) {
  for (let o in __cssModules$4) {
    this[o] = __cssModules$4[o];
  }
}
var SearchTags = /* @__PURE__ */ function() {
  return __component__$4.exports;
}();
var render$3 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "search-result" }, [_vm.result.image ? _c("div", { staticClass: "search-result__img" }, [_c("img", { attrs: { "src": _vm.result.image } })]) : _vm._e(), _c("div", { staticClass: "search-result__content" }, [_c("h6", { staticClass: "search-result__title" }, [_c("a", { attrs: { "target": _vm.openInNewWindow ? "_blank" : false, "href": _vm.result.url } }, [_vm._v(_vm._s(_vm.result.title))])]), _vm.result.itemDate ? _c("small", { staticClass: "search-result__date" }, [_vm._v(_vm._s(_vm.date))]) : _vm._e(), _c("p", { staticClass: "search-result__summary" }, [_vm._v(_vm._s(_vm.result.summary))])])]);
};
var staticRenderFns$3 = [];
var Item_vue_vue_type_style_index_0_scoped_true_lang$1 = "";
const __vue2_script$3 = {
  name: "BlueResultsItem",
  props: ["result", "formatDate", "openInNewWindow"],
  computed: {
    date() {
      return this.formatDate(this.result.itemDate, "MMM dd, yyyy");
    }
  }
};
const __cssModules$3 = {};
var __component__$3 = /* @__PURE__ */ normalizeComponent(__vue2_script$3, render$3, staticRenderFns$3, false, __vue2_injectStyles$3, "b55aac9a", null, null);
function __vue2_injectStyles$3(context) {
  for (let o in __cssModules$3) {
    this[o] = __cssModules$3[o];
  }
}
var Item$1 = /* @__PURE__ */ function() {
  return __component__$3.exports;
}();
var render$2 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("span", { staticClass: "root" }, [_vm._t("default", function() {
    return [_c("TransitionNavigate", [_vm.reloading ? _vm._t("loader", function() {
      return [_c("SearchLoader")];
    }, { "reloading": _vm.reloading }) : _vm.results.length ? _c("div", { staticClass: "search__results" }, [_vm._l(_vm.results, function(result) {
      return _vm.results ? [_vm._t("item", function() {
        return [_c("Item", { attrs: { "result": result, "openInNewWindow": _vm.options.openInNewWindow, "formatDate": _vm.formatDate } })];
      }, { "result": result, "openInNewWindow": _vm.options.openInNewWindow, "formatDate": _vm.formatDate })] : _vm._e();
    })], 2) : [_vm._t("empty", function() {
      return [_c("p", { staticClass: "search__results-empty", domProps: { "innerHTML": _vm._s(_vm.options.noResultsText) } })];
    }, { "noResultsText": _vm.options.noResultsText })]], 2)];
  }, { "results": _vm.results, "reloading": _vm.reloading })], 2);
};
var staticRenderFns$2 = [];
var Results_vue_vue_type_style_index_0_scoped_true_lang = "";
const __vue2_script$2 = {
  name: "BlueResults",
  components: { TransitionNavigate, SearchLoader, Item: Item$1 },
  props: [],
  data() {
    return {};
  },
  computed: {
    results() {
      return this.$store.getters.results.itemResults;
    },
    reloading() {
      if (this.options.transitionOnReload)
        return this.$store.getters.reloading;
      else
        return false;
    }
  },
  methods: {
    formatDate(date, formatPattern) {
      return format(new Date(date), formatPattern);
    },
    getElTop() {
      let elem = this.$root.$el;
      let distance = 0;
      do {
        distance += elem.offsetTop;
        elem = elem.offsetParent;
      } while (elem);
      distance = distance < 0 ? 0 : distance;
      return distance;
    },
    scrollToTop() {
      if (this.options.scrollOnReload)
        window.scrollTo({ top: this.getElTop(), behavior: "smooth" });
    }
  },
  watch: {
    reloading() {
      this.scrollToTop();
    }
  }
};
const __cssModules$2 = {};
var __component__$2 = /* @__PURE__ */ normalizeComponent(__vue2_script$2, render$2, staticRenderFns$2, false, __vue2_injectStyles$2, "4f0631eb", null, null);
function __vue2_injectStyles$2(context) {
  for (let o in __cssModules$2) {
    this[o] = __cssModules$2[o];
  }
}
var SearchResults = /* @__PURE__ */ function() {
  return __component__$2.exports;
}();
var render$1 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.isNumberVisible ? _c(_vm.type, { tag: "component", staticClass: "search-pagination__number", on: { "click": function($event) {
    return _vm.goToPage(_vm.index);
  } } }, [_vm._v(_vm._s(_vm.number))]) : _vm.isEllipseVisible ? _c("span", { staticClass: "search-pagination__ellipsis" }) : _vm._e();
};
var staticRenderFns$1 = [];
var Item_vue_vue_type_style_index_0_scoped_true_lang = "";
const __vue2_script$1 = {
  name: "BluePaginationItem",
  props: ["index", "number", "goToPage", "isActive", "isNumber", "isEllipsis"],
  computed: {
    type() {
      return this.isActive(this.index) ? "span" : "button";
    },
    isNumberVisible() {
      return this.isNumber(this.index);
    },
    isEllipseVisible() {
      return this.isEllipsis(this.index);
    }
  }
};
const __cssModules$1 = {};
var __component__$1 = /* @__PURE__ */ normalizeComponent(__vue2_script$1, render$1, staticRenderFns$1, false, __vue2_injectStyles$1, "12b38210", null, null);
function __vue2_injectStyles$1(context) {
  for (let o in __cssModules$1) {
    this[o] = __cssModules$1[o];
  }
}
var Item = /* @__PURE__ */ function() {
  return __component__$1.exports;
}();
var render = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.totalPages > 0 ? _c("div", { ref: "root", staticClass: "search-pagination", class: { adjusting: _vm.isAdjusting } }, [_vm._t("default", function() {
    return [_c("div", { ref: "container", staticClass: "search-pagination__inner" }, [_vm._t("previous", function() {
      return [_c(_vm.enablePrev ? "button" : "span", { key: "prev", tag: "component", staticClass: "search-pagination__prev", attrs: { "aria-label": "Previous results page" }, on: { "click": function($event) {
        $event.stopPropagation();
        $event.preventDefault();
        _vm.enablePrev ? _vm.navigate(-1) : false;
      } } })];
    }, { "enablePrev": _vm.enablePrev, "navigate": _vm.navigate }), _vm._l(_vm.totalPages, function(number, index) {
      return [_vm._t("item", function() {
        return [_c("Item", { attrs: { "index": index, "number": number, "goToPage": _vm.goToPage, "isActive": _vm.isActive, "isNumber": _vm.isNumber, "isEllipsis": _vm.isEllipsis } })];
      }, { "number": number, "index": index, "goToPage": _vm.goToPage, "isActive": _vm.isActive, "isNumber": _vm.isNumber, "isEllipsis": _vm.isEllipsis })];
    }), _vm._t("next", function() {
      return [_c(_vm.enableNext ? "button" : "span", { key: "next", tag: "component", staticClass: "search-pagination__next", attrs: { "aria-label": "Next results page" }, on: { "click": function($event) {
        $event.stopPropagation();
        $event.preventDefault();
        _vm.enableNext ? _vm.navigate(1) : false;
      } } })];
    }, { "enableNext": _vm.enableNext, "navigate": _vm.navigate })], 2)];
  }, { "totalPages": _vm.totalPages, "currentPage": _vm.currentPage, "enablePrev": _vm.enablePrev, "enableNext": _vm.enableNext, "navigate": _vm.navigate })], 2) : _vm._e();
};
var staticRenderFns = [];
var Pagination_vue_vue_type_style_index_0_scoped_true_lang = "";
const __vue2_script = {
  name: "BluePagination",
  components: { Item },
  data() {
    return {
      hideCount: 0,
      adjusting: false,
      debounceTimer: null
    };
  },
  computed: {
    reloading() {
      return this.$store.getters.reloading;
    },
    isAdjusting() {
      if (!this.options.transitionOnReload && this.options.preserveResults)
        return false;
      else
        return this.adjusting;
    },
    totalPages() {
      return this.$store.getters.results.totalPages;
    },
    currentPage() {
      return this.$store.getters.currentPage;
    },
    enablePrev() {
      return this.currentPage > 0;
    },
    enableNext() {
      return this.currentPage < this.totalPages - 1;
    }
  },
  methods: {
    goToPage(index) {
      this.$store.dispatch("goToPage", index);
    },
    navigate(delta) {
      this.goToPage(this.currentPage + delta);
    },
    isActive(index) {
      return index === this.currentPage;
    },
    isNumber(index) {
      return this.getVisibilityAtIndex(index);
    },
    isEllipsis(index) {
      return this.getVisibilityAtIndex(index - 1);
    },
    async hideOverflow() {
      if (!this.$refs.root || !this.$refs.container) {
        this.adjusting = false;
        return;
      }
      let rootWidth = this.$refs.root.offsetWidth;
      let containerWidth = this.$refs.container.offsetWidth;
      while (rootWidth < containerWidth && this.hideCount < this.totalPages - 5) {
        this.hideCount++;
        await this.$nextTick();
        rootWidth = this.$refs.root.offsetWidth;
        containerWidth = this.$refs.container.offsetWidth;
      }
      applyMax:
        if (this.options.maxPaginationItems) {
          if (this.totalPages <= this.options.maxPaginationItems)
            break applyMax;
          if (this.totalPages - this.hideCount <= this.options.maxPaginationItems)
            break applyMax;
          this.hideCount = this.totalPages - this.options.maxPaginationItems;
        }
      this.adjusting = false;
    },
    getVisibilityAtIndex(index) {
      let visible = true;
      if (this.hideCount > 0) {
        const delta = Math.floor(this.hideCount / 2);
        if (index == 0 || index == this.currentPage || index == this.totalPages - 1) {
          return visible;
        }
        let middle = Math.floor(this.totalPages / 2);
        let leftStart = middle;
        let lowRange = middle - delta;
        let rightStart = middle;
        let highRange = middle + delta;
        let rangeOffset = 0;
        if (this.currentPage >= lowRange - 2 && this.currentPage <= highRange + 2) {
          middle = this.currentPage;
          leftStart = middle - 2;
          lowRange = leftStart - delta;
          rightStart = middle + 2;
          highRange = rightStart + delta;
        }
        if (highRange > this.totalPages)
          rangeOffset = this.totalPages - highRange;
        if (lowRange < 1)
          rangeOffset = Math.abs(1 - lowRange);
        lowRange = lowRange + rangeOffset;
        highRange = highRange + rangeOffset;
        if (index <= leftStart && index >= lowRange)
          visible = false;
        if (index >= rightStart && index <= highRange)
          visible = false;
      }
      return visible;
    },
    async adjustAfterReload() {
      this.hideCount = 0;
      if (this.reloading)
        this.adjusting = true;
      await this.$nextTick();
      if (!this.reloading) {
        this.hideOverflow();
      }
    },
    adjustAfterResize() {
      clearTimeout(this.debounceTimer);
      this.adjusting = true;
      this.debounceTimer = setTimeout(async () => {
        this.hideCount = 0;
        await this.$nextTick();
        this.hideOverflow();
      }, 250);
    }
  },
  watch: {
    reloading() {
      this.adjustAfterReload();
    }
  },
  mounted() {
    this.hideOverflow();
    window.addEventListener("resize", this.adjustAfterResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.adjustAfterResize);
  }
};
const __cssModules = {};
var __component__ = /* @__PURE__ */ normalizeComponent(__vue2_script, render, staticRenderFns, false, __vue2_injectStyles, "60e83b0b", null, null);
function __vue2_injectStyles(context) {
  for (let o in __cssModules) {
    this[o] = __cssModules[o];
  }
}
var SearchPagination = /* @__PURE__ */ function() {
  return __component__.exports;
}();
const plugin = {
  install(Vue, config) {
    let options = Object.fromEntries(Object.entries(config).map(([k, v]) => {
      if (!isNaN(v))
        v = Number(v);
      else if (v.toLowerCase() === "true")
        v = true;
      else if (v.toLowerCase() === "false")
        v = false;
      return [k, v];
    }));
    let defaultOptions = {
      allowPreSearch: false,
      filterAutoSubmit: false,
      filterDrawerCloseText: "Close",
      filterDrawerToggleText: "Filter Results",
      filterInitiallyExpanded: "false",
      filterSubmitText: "Apply",
      hideRail: false,
      maxPaginationItems: null,
      openInNewWindow: true,
      pageLength: 10,
      preserveResults: false,
      queryPlaceholderText: "Search keyword or phrase",
      querySubmitText: "Search",
      resetText: "Reset",
      scrollOnReload: true,
      showFilters: true,
      showFiltersCount: true,
      showQuery: true,
      showReset: false,
      showSort: true,
      showTags: true,
      sortText: "Sort by",
      transitionOnReload: true,
      noResultsText: "Sorry, there were no results found for your search. Please try again."
    };
    options = defaults(options, defaultOptions);
    Vue.mixin({
      data() {
        return {
          options
        };
      }
    });
    Vue.component("SearchQuery", SearchQuery);
    Vue.component("SearchFilters", SearchFilters);
    Vue.component("SearchTags", SearchTags);
    Vue.component("SearchResults", SearchResults);
    Vue.component("SearchPagination", SearchPagination);
    Vue.component("BlueSearch", BlueSearchApp);
  }
};
export { plugin as BlueSearch, store };
